const BASE = '/annotator-api';

function getToken(): string | null {
  return localStorage.getItem('dmo_access_token');
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function uploadTestImages(projectId: number, files: File[]): Promise<{ uploaded: number; filenames: string[] }> {
  const form = new FormData();
  files.forEach((f) => form.append('files', f));
  const res = await fetch(`${BASE}/projects/${projectId}/detection-test/images`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function listTestImages(projectId: number): Promise<{ images: string[]; count: number }> {
  const res = await fetch(`${BASE}/projects/${projectId}/detection-test/images`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/**
 * Fetch an image via Authorization header and return an object URL.
 * Caller must revoke the URL when done via URL.revokeObjectURL().
 */
export async function fetchImageBlob(url: string): Promise<string> {
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export function testImagePath(projectId: number, filename: string): string {
  return `${BASE}/projects/${projectId}/detection-test/images/${encodeURIComponent(filename)}`;
}

export function testImageThumbPath(projectId: number, filename: string): string {
  return `${BASE}/projects/${projectId}/detection-test/images/${encodeURIComponent(filename)}/thumb`;
}

export async function clearTestImages(projectId: number): Promise<void> {
  const res = await fetch(`${BASE}/projects/${projectId}/detection-test/images`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function listTrainedModels(projectId: number): Promise<{ category: string; classes: { index: number; name: string }[]; has_model: boolean }> {
  const res = await fetch(`${BASE}/projects/${projectId}/detection-test/models`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function runDetection(
  projectId: number,
  confidence: number = 0.25,
  category: string = '',
  classes?: number[],
): Promise<{ successful: number; failed: number; category: string }> {
  const body: Record<string, any> = { confidence, category };
  if (classes !== undefined) body.classes = classes;
  const res = await fetch(`${BASE}/projects/${projectId}/detection-test/run`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function listResults(projectId: number): Promise<{ results: string[]; count: number }> {
  const res = await fetch(`${BASE}/projects/${projectId}/detection-test/results`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export function resultImagePath(projectId: number, filename: string): string {
  return `${BASE}/projects/${projectId}/detection-test/results/${encodeURIComponent(filename)}`;
}

export function resultImageThumbPath(projectId: number, filename: string): string {
  return `${BASE}/projects/${projectId}/detection-test/results/${encodeURIComponent(filename)}/thumb`;
}

export async function clearResults(projectId: number): Promise<void> {
  const res = await fetch(`${BASE}/projects/${projectId}/detection-test/results`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}
